import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { connect } from 'react-redux';
import asyncDispatch from '../../../../../services/mini/asyncDispatch';
import * as projectActions from '../../../../../redux/projects/actions.projects';
import * as unloadPageActions from '../../../../../redux/unloadPage/actions.unloadPage';
import { CardElement, injectStripe } from 'react-stripe-elements';
import TextFieldWrapper from '../../../../components/TextFieldWrapper/TextFieldWrapper';
import stripeLogoSmall from '../../../../../assets/images/billing/stripe-logo-small.svg';
import capitalize from '../../../../../services/mini/capitalize';
import { Loader } from '../../../../components/Loader/Loader';
import getErrorMsg from '../../../../../services/mini/getErrorMsg';
import { BILLING_STRIPE } from '../../../../../const/billingTypes';
import billingEvents from '../../../../../const/billingEvents';
import ToasterService from '../../../../../services/ToasterService';
import Money from '../../../../components/Money/Money';
import { BillingAmounts } from '../../BillingAmounts/BillingAmounts';
import StripeSecureInfo from '../../StripeSecureInfo/StripeSecureInfo';
import GaService from '../../../../../services/GaService';
import { getDefaultAmount } from '../../getDefaultAmount';

const style = {
    'base': {
        'fontFamily': 'Lato',
        'fontSize': '14px',
        'fontWeight': 'normal',
        'color': '#0f2029',
        '::placeholder': {
            color: '#6a85a6',
        },
    },
    'invalid': {
        'color': '#e24545',
    },
};

class StripeForm extends React.Component {
    constructor(props, context) {
        super(props, context);

        this.state = {
            amount: getDefaultAmount(this.props.amount),
            amountFieldError: null,
            amountFieldInfo: null,

            name: '',
            nameFieldError: null,
            nameFieldInfo: null,

            country: '',
            countryFieldError: null,
            countryFieldInfo: null,

            city: '',
            cityFieldError: null,
            cityFieldInfo: null,

            state: '',
            stateFieldError: null,
            stateFieldInfo: null,

            address: '',
            addressFieldError: null,
            addressFieldInfo: null,

            cardFieldError: null,

            payingIsInProcess: false,
            cardLabelVisible: false,
        };

        this.maxFieldLength = 500;
    }

    getTrackInfo = (status, amount = 0) => {
        const {
            isInitialBilling,
            contentId,
            contentVersion
        } = this.context;

        return {
            paymentMethod: BILLING_STRIPE,
            isInitialBilling: isInitialBilling,
            contentId,
            contentVersion,
            paymentStatus: status,
            paymentAmount: amount
        };
    }

    trackPayment = (status, amount) => {
        const {
            trackPaymentStart = _.noop(),
        } = this.context;

        trackPaymentStart(this.getTrackInfo(status, amount));
    }

    submit = async () => {
        const {
            getStripePaymentIntent,
            proceedStripePayment,
            refund,
            onBillingFinishedCb,
            nextPlan,
            campaignName,
        } = this.props;

        const {
            amount,
        } = this.state;

        const cardElement = this.props.elements.getElement('card');

        const {
            trackPaymentError = _.noop(),
            trackPaymentSuccess = _.noop(),
        } = this.context;

        this.setState({ payingIsInProcess: true });

        if (!this.validate()) {
            this.setState({ payingIsInProcess: false });
            this.trackPayment(billingEvents.clientValidationFailed);
            return;
        }

        this.trackPayment(billingEvents.paymentBtnClicked, +amount);

        getStripePaymentIntent({ amount: +amount * 100 }).then(async (clientSecret) => {
            await this.props.stripe.confirmCardPayment(clientSecret, {
                payment_method: {
                    card: cardElement,
                },
                setup_future_usage: 'off_session'
            }).then((result) => {
                if (result.error) {
                    ToasterService.showError({ title: result.error.message });
                    this.setState({ payingIsInProcess: false });
                    trackPaymentError(this.getTrackInfo(billingEvents.getStripePaymentIntentError + getErrorMsg(result.error)));
                } else {
                    if (result.paymentIntent.status === 'succeeded') {
                        proceedStripePayment({
                            stripePaymentIntentId: result.paymentIntent.id,
                            refund: refund,
                            nextPlanId: nextPlan?.id
                        }).then(() => {
                            trackPaymentSuccess({ ...this.getTrackInfo(), paymentAmount: +amount });
                            GaService.sendPaymentEvent({
                                amount,
                                paymentType: BILLING_STRIPE,
                                campaignName,
                            });
                            onBillingFinishedCb(result.paymentIntent.id);
                        }, (chargeUserErr) => {
                            this.setState({ payingIsInProcess: false });
                            trackPaymentError(this.getTrackInfo(billingEvents.proceedStripePaymentError + getErrorMsg(chargeUserErr)));
                        });
                    }
                }
            });
        });
    }

    validate = (validationType) => {
        let amountFieldError = null;
        let amountFieldInfo = null;
        const { cardFieldError } = this.state;

        if (validationType === 'amount') {
            amountFieldError = this.validateAmount();
            amountFieldInfo = this.validateFieldLength(validationType);
        }
        else {
            amountFieldError = this.validateAmount();
            amountFieldInfo = this.validateFieldLength('amount');
        }

        this.setState({
            amountFieldError,
            amountFieldInfo,
        });

        return amountFieldError === null &&
            amountFieldInfo === null &&
            cardFieldError === null;
    }

    validateAmount = () => {
        let { amount } = this.state;

        amount = +amount;

        if (isNaN(amount))
            return 'Amount is not valid';

        if (amount <= 0) {
            return 'Min amount is $1';
        }

        if (amount > 9999) {
            return 'Max amount is $9999';
        }
        return null;
    }

    validateField = (field) => {
        const fieldVal = this.state[field];
        let fieldError = null;

        if (!fieldVal) {
            if (field === 'name') {
                fieldError = 'Name on card should not be empty';
            } else {
                fieldError = capitalize(`${field} should not be empty`);
            }
        }

        return fieldError;
    }

    validateFieldLength = (fieldType) => {
        const field = this.state[fieldType];
        let fieldInfo = null,
            type;
        if (field.length > this.maxFieldLength) {
            if (!this.state[fieldType + 'FieldLength']) {
                this.setState({ [fieldType + 'FieldLength']: field.length });
            }
            if (fieldType === 'name') {
                type = 'name';
            }
            return `Max ${type} length: ${this.maxFieldLength} characters`;
        }

        this.setState({ [fieldType + 'FieldLength']: null });

        return fieldInfo;
    }

    onLengthValidate = () => {
        this.validate();
    }

    onCardElementFocus = () => {
        this.setState({ cardLabelVisible: true });
    }

    onTextInputChange = (e) => {
        const field = e.target.name;
        const fieldValue = e.target.value;
        const re = /^[0-9\b]+$/;
        let newState = this.state;

        if (field === 'amount') {
            if (fieldValue === '' || re.test(fieldValue)) {
                newState[field] = fieldValue;
            }
        } else {
            newState[field] = fieldValue;
        }

        this.setState(newState);
    }

    onTextInputBlur = (validationType) => {
        this.validate(validationType);
    }

    onCardElementChange = (changeObject) => {
        if (changeObject.error && changeObject.error.message) {
            let cardElErr = changeObject.error.message;
            // Delete a dot from the error messages which we receive from stripe manually, because we display all other messages without dots
            if (cardElErr[cardElErr.length - 1] === '.') {
                cardElErr = cardElErr.substr(0, cardElErr.length - 1);
            }
            this.setState({ cardFieldError: cardElErr });
        } else {
            this.setState({ cardFieldError: null });
        }
    }

    render() {
        const {
            amount,
            name,
            address,
            payingIsInProcess,
            cardFieldError,
            cardLabelVisible,
            amountFieldError,
            amountFieldInfo,
            nameFieldError,
            nameFieldInfo,
            addressFieldError,
            addressFieldInfo,
        } = this.state;

        const {
            readOnlyAmount,
            stripeBtnTitle
        } = this.props;

        return (
            <div className="stripe-form">
                {payingIsInProcess &&
                    <Loader
                        theme="dark"
                        size="medium"
                        center={true}
                        appearWithFade={true} />}
                <div>
                    {
                        readOnlyAmount ?
                            <React.Fragment>
                                <div className="billing-amount-readonly">
                                    <Money amount={amount} />
                                </div>
                                {this.props.infoMsg && <div className="stripe-form__info-msg">{this.props.infoMsg}</div>}
                            </React.Fragment> :
                            <React.Fragment>
                                {this.props.infoMsg && <div className="stripe-form__info-msg">{this.props.infoMsg}</div>}
                                <BillingAmounts amount={amount} onSelect={(newAmount) => {
                                    this.setState({ amount: newAmount });
                                }} />
                            </React.Fragment>
                    }
                    <div className={`${payingIsInProcess && 'disabled'}`}>
                        <div className="stripe-form__card-holder">
                            <div className={`stripe-form__card-label required ${cardLabelVisible && 'visible-with-opacity'}`} title="Required field">
                                {cardFieldError || 'Card info'}
                            </div>
                            <CardElement className="stripe-form__card"
                                style={style}
                                onChange={this.onCardElementChange}
                                onFocus={this.onCardElementFocus}
                            />
                        </div>

                        <TextFieldWrapper
                            placeholder="Name on card"
                            label="Name on card"
                            name="name"
                            id="name"
                            className="stripe-form__name text-field-with-left-margin"
                            value={name}
                            error={nameFieldError}
                            info={nameFieldInfo}
                            onChange={this.onTextInputChange}
                            onBlur={this.onTextInputBlur} />


                        <TextFieldWrapper
                            placeholder="Billing Address"
                            label="Billing Address"
                            name="address"
                            id="address"
                            className="stripe-form__address text-field-with-left-margin"
                            value={address}
                            error={addressFieldError}
                            info={addressFieldInfo}
                            onChange={this.onTextInputChange}
                            onBlur={this.onTextInputBlur} />
                    </div>

                    <div className="popup-form_btns">
                        <div onClick={this.submit}
                            className={`popup-form_btn action-btn big only-btn ${payingIsInProcess && 'disabled'}`}>
                            {stripeBtnTitle || 'Submit Payment'}
                        </div>
                    </div>
                    <StripeSecureInfo disabled={payingIsInProcess} />
                </div>
            </div>
        );
    }
}

StripeForm.contextTypes = {
    trackPaymentStart: PropTypes.func,
    trackPaymentError: PropTypes.func,
    trackPaymentSuccess: PropTypes.func,
    isInitialBilling: PropTypes.bool,
    contentId: PropTypes.string,
    contentVersion: PropTypes.string,
};


function mapStateToProps({ user }, ownProps) {
    return {
        balance: user.balance,
        campaignName: user.campaignName,
        loginType: user.loginType,
    };
}

function mapDispatchToProps(dispatch) {
    return {
        sendBillingToken: (param) => asyncDispatch(projectActions.sendBillingToken, dispatch, param),
        getStripePaymentIntent: (param) => asyncDispatch(projectActions.getStripePaymentIntent, dispatch, param),
        proceedStripePayment: (param) => asyncDispatch(projectActions.proceedStripePayment, dispatch, param),
        updateUnloadPageListenerForBilling: (param) => dispatch(unloadPageActions.updateUnloadPageListenerForBilling(param)),
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(injectStripe(StripeForm));
